import { Component, h, Prop, State, Host, Watch, Listen, Fragment } from '@stencil/core';
import { minus, close, arrow_right, talk_bubble, submit } from 'pn-design-assets/pn-assets/icons.js';
import { AgentMessage, BaseMessage, ButtonMessage, CardMessage, ChatMessage, ChatMessages, CustomerSegment, LinkMessage } from './types';
import { LocalStorageService } from '@/globals/LocalStorageService';
import { JSX } from 'react';
import { PnChatService } from './pn-chat.service';
import { translations } from './translations';
import type { Translationsi18n } from './types';

@Component({
  tag: 'pn-chat',
  styleUrl: 'pn-chat.scss',
})
export class PnChat {
  @Prop() endpoint!: string;
  @Prop() customerSegment!: CustomerSegment;
  @Prop() mainHeading: string = '';
  @Prop() relatedContentAriaLabel: string;
  @Prop() chatMinimizedAriaLabel: string;
  @Prop() chatOpenedAriaLabel: string;
  @Prop() minimizeChatAriaLabel: string;
  @Prop() closeChatAriaLabel: string;
  @Prop() openChatAriaLabel: string;
  @Prop() chatControlsAriaLabel: string;
  @Prop() chatMessagesAriaLabel: string;
  @Prop() sentMessageAriaLabel: string;
  @Prop() writeChatMessagePlaceholder: string;
  @Prop() waitingForAnswerLabel: string;
  @Prop() sendButtonText: string;
  @Prop() loadingText: string;
  @Prop() carouselSlidesToShow: number = 1;
  @Prop() messages: string;
  @Prop() language: string = 'sv';
  @Prop() market: string = 'se';
  @Prop() itemId?: string = '';
  @Prop() pnChatId: string = '00000000-0000-0000-0000-000000000000';
  @Prop() forceOpen: boolean = false;
  @Prop() isLoggedIn: boolean = false;

  @State() private message: string = '';
  @State() private messagesData: ChatMessages;
  @State() private isMinimized: boolean = true;
  @State() private isClosed: boolean = false;
  @State() private lastMessageId: string = '';
  @State() i18n: any;

  private chatId: string;

  private readonly _defaultChatId: string = '00000000-0000-0000-0000-000000000000';
  private readonly _defaultAlternativeChatId: string = '1';
  private readonly chatLocalStorageKey = 'sf-chat-state';
  private chatMessagesContainer?: HTMLElement;
  private chatRef?: HTMLDivElement;
  private inputRef?: HTMLInputElement;
  private storage: LocalStorageService = new LocalStorageService();
  private _chatService: PnChatService;
  private _chatSegment: string = "#chat";
  private _forceClose: boolean = false;

  componentWillLoad(): void {
    this._forceClose = false;
    this._chatService = new PnChatService(this.endpoint, this.market, this.language);
    this.messagesData = {
      header: {},
      messages: [],
    };
    let storageId = this.storage.get(this.chatLocalStorageKey);

    if (storageId && storageId.isLoggedIn === !this.isLoggedIn) {
      this.storage.remove(this.chatLocalStorageKey);
    }
    else if (storageId && storageId.chatId) {
      this.chatId = storageId?.chatId ?? this.chatId;
    }

    this.setTranslations();

    setInterval(() => {
      this.updateChat();
    }, 1500);
  }

  setTranslations() {
    if (this.language && translations[this.language]) {
      this.i18n = translations[this.language];
    }
  }

  private getTranslation(key: keyof Translationsi18n): string {
    const value = this[key];
    if (value === undefined || value === null || value === '') {
      return translations[this.language]?.[key] ?? translations['en'][key];
    }
    return value;
  }

  private async updateChat() {
    if (this.isMinimized || this.isClosed) {
      return;
    }

    let prevLastMessageId = this.lastMessageId;

    await this._chatService.fetchMessages(this.chatId, this.messagesData).then(newMessages => {
      this.lastMessageId = this._chatService.GetLastMessageId(newMessages?.messages, this.lastMessageId);
      let htmlCollection = document.getElementsByClassName('pn-chat-before');
      let elements = Array.from(htmlCollection);

      newMessages.messages.forEach(message => {
        elements.forEach(element => {
          if (message.content != null && (element.textContent.trim() === message.content.trim() || element.textContent.trim().includes(message.content.trim()))) {
            element.remove();
          }
        });
        this.renderMessage(message);
      });

      if (prevLastMessageId !== this.lastMessageId) {
        this._chatService.updateLastMessageId(this.chatId, this.lastMessageId);
        this.messagesData = newMessages;
        newMessages.messages.forEach(message => {
          this.renderMessage(message);
        });
      }
    });
  }

  private async setStartValues() {
    if (this._forceClose) {
      this._forceClose = false;
      return;
    }

    if ((!this.chatId || this.chatId === this._defaultChatId || this.chatId === this._defaultAlternativeChatId) && !this.isMinimized) {
      this.chatId = await this._chatService.startChat(this.pnChatId, this.itemId);
      this.storage.set(this.chatLocalStorageKey, { chatId: this.chatId, isLoggedIn: this.isLoggedIn });
    }

    if (this.chatId) {
      this.storage.set(this.chatLocalStorageKey, { chatId: this.chatId, isLoggedIn: this.isLoggedIn });
      await this.fetchMessages();
      this.lastMessageId = this._chatService.GetLastMessageId(this.messagesData.messages, this.lastMessageId);
    }
  }

  private async fetchMessages() {
    let newMessages = await this._chatService.fetchMessages(this.chatId, this.messagesData).then(newMessages => {
      this.lastMessageId = this._chatService.GetLastMessageId(this.messagesData.messages, this.lastMessageId);
      this.messagesData = newMessages;
      let htmlCollection = document.getElementsByClassName('pn-chat-before');
      let elements = Array.from(htmlCollection);

      newMessages.messages.forEach(message => {
        elements.forEach(element => {
          if (element.textContent.trim() === message.content.trim() || element.textContent.trim().includes(message.content.trim())) {
            element.remove();
          }
        });
        this.renderMessage(message);
      });
      return newMessages;
    });
  }

  private async handleSend() {
    if (!this.message?.trim()) {
      return;
    }

    let message = this.message;
    this.message = '';
    this.createBeforeElement(message);

    let responseId = await this._chatService.sendMessage(message, this.itemId, this.chatId, this.lastMessageId, 'Text');
    this.lastMessageId = responseId;
    this.lastMessageId = this._chatService.GetLastMessageId(this.messagesData.messages, this.lastMessageId);

    this.announceToScreenReader(this.getTranslation('sentMessageAriaLabel'));
  };

  private groupMessages(): JSX.Element[] {
    const result: JSX.Element[] = [];
    let currentCardGroup: CardMessage[] = [];

    const flushCardGroup = () => {
      if (currentCardGroup.length > 0) {
        const carouselData = {
          slides: currentCardGroup.map(card => {
            const cardHtml = `
              <pn-chat-message customerSegment=${this.customerSegment} data-card role="article" category=${card.category}>
                <h2 class="card-heading">${card.heading}</h2>
                <p>${card.content}</p>
                ${card.buttons
                ?.map(
                  button => `
                  <pn-button
                    href="${button.href}"
                    class="${button.class}"
                    appearance="${button.appearance}"
                    variant="${button.variant}"
                    small="${button.small}"
                    role="button"
                  >
                    ${button.content}
                  </pn-button>
                `,
                )
                .join('') || ''
              }
              </pn-chat-message>
            `;
            return cardHtml;
          }),
        };
        result.push(
          <pn-marketweb-carousel
            class="card-container"
            nextButtonLabel="Next"
            previousButtonLabel="Previous"
            ariaNavigationLabel={this.getTranslation('relatedContentAriaLabel')}
            slidesToShow={this.carouselSlidesToShow}
            source={JSON.stringify(carouselData)}
          ></pn-marketweb-carousel>,
        );
        currentCardGroup = [];
      }
    };

    this.messagesData.messages.forEach((message, index) => {
      if (message.category === 'Card') {
        currentCardGroup.push(message as CardMessage);

        if (index === this.messagesData.messages.length - 1 || this.messagesData.messages[index + 1]?.category !== 'Card') {
          flushCardGroup();
        }
      } else {
        flushCardGroup();
        result.push(this.renderMessage(message));
      }
    });

    return result;
  }

  private handleKeyPress = (event: KeyboardEvent) => {
    if (event.key === 'Enter' && this.message.trim()) {
      event.preventDefault();
      this.handleSend();
    }
  };

  private handleSendMessage = (event: MouseEvent) => {
    if (this.message.trim()) {
      event.preventDefault();
      this.handleSend();
    }
  }

  private handleButtonKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      (event.target as HTMLElement).click();
    }
  }

  @Listen('keydown')
  handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape' && this.isMinimized && !this.isClosed) {
      this.toggleChat();
    }
  }

  @Watch('messages')
  handleMessagesChange(newMessage: string): void {
    if (newMessage) {
      this.handleSend();
    }
  }

  @Watch('pnChatId')
  handleNewChatId() {
    this.chatId = this.pnChatId === this._defaultAlternativeChatId ? this._defaultChatId : this.pnChatId;
    this.messagesData.messages = [];
    this.isMinimized = false;
    this.setStartValues();
  }

  @Watch('forceOpen')
  handleSameChatId() {
    this.isMinimized = false;
  }

  @Watch('isMinimized')
  scrollToBottom() {
    const observer = new MutationObserver((_, obs) => {
      const chatMain = document.getElementById('chat-main');
      if (chatMain) {
        setTimeout(() => {
          chatMain.scrollTo({
            top: chatMain.scrollHeight,
            behavior: 'smooth'
          });
        }, 500)
        obs.disconnect();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true, });
  }

  private closeChat = () => {
    this._forceClose = true;
    this.storage.remove(this.chatLocalStorageKey);
    this.chatId = null;
    this.pnChatId = this._defaultChatId;
    let chatElement = document.getElementsByTagName('pn-chat')[0];
    chatElement.setAttribute('item-id', '');

    let hasActiveShipmentInUrl = window.location.href.includes(this._chatSegment);
    if (hasActiveShipmentInUrl) {
      this.isMinimized = !this.isMinimized;
      this.announceToScreenReader(this.isMinimized ? this.getTranslation('chatMinimizedAriaLabel') : this.getTranslation('chatOpenedAriaLabel'));
      document.body.classList.toggle('chat-open');
      window.location.href = window.location.href.split(this._chatSegment)[0];
    }
    else {
      this._forceClose = false;
      this.toggleChat();
    }
  }

  private toggleChat = () => {
    this.isMinimized = !this.isMinimized;
    this.announceToScreenReader(this.isMinimized ? this.getTranslation('chatMinimizedAriaLabel') : this.getTranslation('chatOpenedAriaLabel'));
    document.body.classList.toggle('chat-open');

    if (!this.isMinimized && !this.chatId) {
      this.setStartValues();
    }
  };

  private announceToScreenReader(message: string) {
    const announcement = document.createElement('div');
    announcement.setAttribute('role', 'status');
    announcement.setAttribute('aria-live', 'polite');
    announcement.classList.add('sr-only');
    announcement.textContent = message;
    document.body.appendChild(announcement);
    setTimeout(() => announcement.remove(), 1000);
  }

  private renderMessage(message: ChatMessage): JSX.Element {
    switch (message.category) {
      case 'Bot':
        return this.renderBaseMessage(message);
      case 'Button':
        return this.renderButtonMessage(message);
      case 'ButtonResponse':
        return this.renderBaseMessage(message);
      case 'Link':
        return this.renderLinkMessage(message);
      case 'Card':
        return this.renderCardMessage(message);
      case 'User':
        return this.renderBaseMessage(message);
      case 'Agent':
        return this.renderAgentMessage(message);
      case 'System':
        return this.renderBaseMessage(message);
    }
  }

  private renderBaseMessage(message: BaseMessage): JSX.Element {
    return (
      <pn-chat-message customerSegment={this.customerSegment} category={message.category} agentName='' role="log" aria-live="polite">
        {message.content}
      </pn-chat-message>
    );
  }

  private renderAgentMessage(message: AgentMessage): JSX.Element {
    return (
      <pn-chat-message customerSegment={this.customerSegment} category={message.category} agentName={message.agentName} role="log" aria-live="polite">
        {message.content}
      </pn-chat-message>
    );
  }

  private renderButtonMessage(message: ButtonMessage): JSX.Element {
    let props = {};

    if (message?.props) {
      try {
        props = JSON.parse(message.props);
      } catch (error) {
        console.error('Error parsing button props', error instanceof Error ? error.message : 'Unknown error');
        return;
      }
    }

    const handleClick = (event: MouseEvent) => {
      const button = (event.target as HTMLElement).closest('button');

      if (button === null || button?.hasAttribute('disabled')) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      if (props['data-action'] === 'minimize') {
        this.toggleChat();
      }

      if (props['data-action'] === 'choice') {
        (async () => {
          await this.handleSendChoice(props['data-option-choice'], message.content);
        })();

        const choiceButtons = document.querySelectorAll('[data-action="choice"]');
        choiceButtons.forEach(pnButton  => {
          const buttonInner = pnButton.querySelector('button');
          buttonInner?.setAttribute('disabled', 'true');
        });
      }
    };

    return (
      <pn-chat-message customerSegment={this.customerSegment} category={message.category}>
        <pn-button class={message.class} role="button" {...props} onClick={handleClick} onKeyDown={e => this.handleButtonKeyDown(e)}>
          {message.content}
        </pn-button>
      </pn-chat-message>
    );
  }

  private createBeforeElement(content: string) {
    const targetElement = this.chatMessagesContainer;
    const chatMessage = document.createElement('pn-chat-message');
    chatMessage.setAttribute('customerSegment', this.customerSegment);
    chatMessage.setAttribute('category', 'User');
    chatMessage.classList.add('pn-chat-before');
    chatMessage.textContent = content;

    targetElement.appendChild(chatMessage);
    this.scrollToBottom();
    chatMessage.scrollIntoView();
  }

  private async handleSendChoice(choiceId: string, content: string) {
    this.createBeforeElement(content);

    let responseId = await this._chatService.sendMessage(choiceId, this.itemId, this.chatId, this.lastMessageId, 'Choice');
    this.lastMessageId = responseId;
    this.lastMessageId = this._chatService.GetLastMessageId(this.messagesData.messages, this.lastMessageId);

    this.announceToScreenReader(this.getTranslation('sentMessageAriaLabel'));
  }

  private renderLinkMessage(message: LinkMessage): JSX.Element {
    return (
      <pn-chat-message customerSegment={this.customerSegment} category={message.category}>
        <a href={message.href} role="link" rel="noopener noreferrer">
          <span class="content">{message.content}</span>
          <pn-icon icon={arrow_right} color="blue700" aria-hidden="true" role="presentation" />
        </a>
      </pn-chat-message>
    );
  }

  private renderCardMessage(message: CardMessage): JSX.Element {
    return (
      <pn-chat-message customerSegment={this.customerSegment} category={message.category} data-card role="article">
        <h2 class="card-heading">{message.heading}</h2>
        <p>{message.content}</p>
        {message.buttons && (
          <div class="cta">
            {message.buttons?.map(button => (
              <pn-button
                href={button.href}
                class={button.class}
                appearance={button.appearance}
                variant={button.variant}
                small={button.small}
                role="button"
                onKeyDown={e => this.handleButtonKeyDown(e)}
              >
                {button.content}
              </pn-button>
            ))}
          </div>
        )}
      </pn-chat-message>
    );
  }

  render() {
    const mainId = 'chat-main';
    const inputId = 'chat-input';
    const headerButton = this.messagesData?.header.button;

    return (
      <Host class={this.customerSegment}>
        {!this.isClosed && this.messagesData && (
          <div
            class={`pn-chat ${this.isMinimized ? 'minimized' : ''} ${this.isClosed ? 'closed' : ''}`}
            ref={el => (this.chatRef = el as HTMLDivElement)}
            role="region"
            aria-label={this.mainHeading}
          >
            {!this.isMinimized && (
              <Fragment>
                <header class="chat-header" role="banner">
                  <h1 id="chat-title">{this.mainHeading}</h1>
                  <div class="chat-controls" role="toolbar" aria-label={this.getTranslation('chatControlsAriaLabel')}>
                    <pn-button
                      icon={minus}
                      icon-only="true"
                      class="minimize-btn"
                      onClick={this.toggleChat}
                      aria-expanded={!this.isMinimized}
                      aria-controls={mainId}
                      aria-label={this.getTranslation('minimizeChatAriaLabel')}
                      appearance="light"
                      tooltip={this.getTranslation('minimizeChatAriaLabel')}
                      small="true"
                    />
                    <pn-button
                      icon={close}
                      icon-only="true"
                      class="close-btn"
                      onClick={this.closeChat}
                      aria-expanded={!this.isMinimized}
                      aria-label={this.getTranslation('closeChatAriaLabel')}
                      appearance="light"
                      tooltip={this.getTranslation('closeChatAriaLabel')}
                      small="true"
                    />
                  </div>
                </header>
                {(this.messagesData?.messages?.length ?? 0) > 0 ?
                  (<main id={mainId} class="chat-main" role="main" aria-labelledby="chat-title">
                    {this.messagesData.header && (
                      <div class="chat-overview">
                        <div class="body">{this.messagesData.header.body}</div>
                        {headerButton && (
                          <pn-button
                            href={headerButton.href}
                            class={headerButton.class}
                            appearance={headerButton.appearance}
                            variant={headerButton.variant}
                            small={headerButton.small}
                            role="button"
                            onKeyDown={e => this.handleButtonKeyDown(e)}
                          >
                            {headerButton.content}
                          </pn-button>
                        )}
                      </div>
                    )}
                    <div ref={el => (this.chatMessagesContainer = el as HTMLElement)} class="chat-messages" role="log" aria-live="polite" aria-label={this.getTranslation('chatMessagesAriaLabel')}>
                      {this.groupMessages()}
                    </div>
                    <pn-icon
                      class="indicator-icon"
                      aria-label={this.getTranslation('waitingForAnswerLabel')}
                      role="presentation"
                    ></pn-icon>
                  </main>)
                  : (<main id={mainId} class="chat-main loading-chat-main" role="main" aria-labelledby="chat-title">
                    <div class="chat-loading-spinner">
                      <pn-spinner light="true" size="1.5"></pn-spinner>
                    </div>
                    <span class="chat-loading-content">{this.getTranslation('loadingText')}</span>
                  </main>)}
                <footer class="chat-footer" role="contentinfo">
                  <div class="form" role="form" aria-labelledby={inputId}>
                    <label htmlFor={inputId} class="sr-only">
                      {this.getTranslation('writeChatMessagePlaceholder')}
                    </label>
                    <div class="chat-input-container">
                      <input
                        id={inputId}
                        class="chat-text-input"
                        ref={el => (this.inputRef = el as HTMLInputElement)}
                        type="text"
                        placeholder={this.getTranslation('writeChatMessagePlaceholder')}
                        value={this.message}
                        onInput={(e: Event) => (this.message = (e.target as HTMLInputElement).value)}
                        onKeyPress={this.handleKeyPress}
                        maxLength={3990}
                      />
                      {!this.message.trim() ? (
                        <div
                          class="chat-disabled-button"
                          role="button"
                          aria-label={this.getTranslation('sendButtonText')}
                          aria-disabled="true"
                          tabIndex={-1}>
                          <pn-icon color="gray400" icon={submit}></pn-icon>
                        </div>
                      ) : (
                        <pn-button
                          icon-only="true"
                          variant="borderless"
                          appearance="light"
                          icon={submit}
                          onClick={this.handleSendMessage}
                          disabled={!this.message.trim()}
                          aria-label={this.getTranslation('sendButtonText')}
                          tooltip={this.getTranslation('sendButtonText')}>
                        </pn-button>
                      )}
                    </div>
                  </div>
                </footer>
              </Fragment>
            )}
            {this.isMinimized && (
              <pn-button
                icon={talk_bubble}
                icon-only="true"
                class="open-chat-btn"
                onClick={this.toggleChat}
                tooltip={this.getTranslation('openChatAriaLabel')}
                aria-label={this.getTranslation('openChatAriaLabel')}
                aria-expanded={!this.isMinimized}
              ></pn-button>
            )}
          </div>
        )}
      </Host>
    );
  }
}